import React, { FunctionComponent } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Radial from "../../Charts/Radial";
import { ChartTitle } from "../styles/employer-analytics-styles";
import { Wrapper, Charts, Chart, ChartName } from "../styles/fill-rate-styles";

export interface IFillRateProps {
  totalFillRate: number;
  pslFillRate: number;
  marketplaceFillRate: number;
  pdfExport?: boolean;
}

export const FillRate: FunctionComponent<IFillRateProps> = ({
  totalFillRate,
  pslFillRate,
  marketplaceFillRate,
  pdfExport,
}) => {
  const totalFillRateOptions = {
    series: [totalFillRate],
    options: {
      height: 170,
      chart: {
        type: "radialBar",
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        radialBar: {
          startAngle: -90,
          endAngle: 90,
          hollow: {
            margin: 0,
            size: "70%",
            background: "#fff",
            image: undefined,
            imageOffsetX: 0,
            imageOffsetY: 0,
            position: "front",
          },
          track: {
            background: "#fff",
            strokeWidth: "95%",
            margin: 0,
            dropShadow: {
              enabled: true,
              top: -2,
              left: 0,
              blur: 4,
              opacity: 0.2,
            },
          },
          dataLabels: {
            show: true,
            name: {
              offsetY: 0,
              show: true,
              color: "#888",
              fontSize: "15px",
            },
            value: {
              formatter: (val: number) => `${val}%`,
              color: "#111",
              offsetY: -20,
              fontSize: "28px",
              show: true,
            },
          },
        },
      },
      colors: ["rgb(0, 143, 251)"],
      labels: [""],
    },
  };

  const pslFillRateOptions = {
    series: [pslFillRate],
    options: {
      height: 170,
      chart: {
        type: "radialBar",
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        radialBar: {
          startAngle: -90,
          endAngle: 90,
          hollow: {
            margin: 0,
            size: "70%",
            background: "#fff",
            image: undefined,
            imageOffsetX: 0,
            imageOffsetY: 0,
            position: "front",
          },
          track: {
            background: "#fff",
            strokeWidth: "95%",
            margin: 0,
            dropShadow: {
              enabled: true,
              top: -2,
              left: 0,
              blur: 4,
              opacity: 0.2,
            },
          },
          dataLabels: {
            show: true,
            name: {
              offsetY: 0,
              show: true,
              color: "#888",
              fontSize: "15px",
            },
            value: {
              formatter: (val: number) => `${val}%`,
              color: "#111",
              offsetY: -20,
              fontSize: "28px",
              show: true,
            },
          },
        },
      },
      colors: ["#79D9D9"],
      labels: [""],
    },
  };

  const marketplaceFillRateOptions = {
    series: [marketplaceFillRate],
    options: {
      height: 170,
      chart: {
        type: "radialBar",
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        radialBar: {
          startAngle: -90,
          endAngle: 90,
          hollow: {
            margin: 0,
            size: "70%",
            background: "#fff",
            image: undefined,
            imageOffsetX: 0,
            imageOffsetY: 0,
            position: "front",
          },
          track: {
            background: "#fff",
            strokeWidth: "95%",
            margin: 0,
            dropShadow: {
              enabled: true,
              top: -2,
              left: 0,
              blur: 4,
              opacity: 0.2,
            },
          },
          dataLabels: {
            show: true,
            name: {
              offsetY: 0,
              show: true,
              color: "#888",
              fontSize: "15px",
            },
            value: {
              formatter: (val: number) => `${val}%`,
              color: "#111",
              offsetY: -20,
              fontSize: "28px",
              show: true,
            },
          },
        },
      },
      colors: ["rgb(0, 227, 150)"],
      labels: [""],
    },
  };
  return (
    <Wrapper className="animated fadeIn" pdfExport={pdfExport}>
      <ChartTitle marginBottom={5} data-test-id="fill-rate-charts-title">
        Fill Rate
      </ChartTitle>
      <Charts data-test-id="fill-rate-charts" pdfExport={pdfExport}>
        <Chart className="animated fadeIn delay-2">
          <Radial
            graphOptions={totalFillRateOptions}
            dataTestId="total-fill-rate"
          />
          <ChartName>Total fill rate</ChartName>
        </Chart>
        <Chart className="animated fadeIn delay-2">
          <Radial
            graphOptions={pslFillRateOptions}
            dataTestId="psl-fill-rate"
          />
          <ChartName>PSL fill rate</ChartName>
        </Chart>
        <Chart className="animated fadeIn delay-2">
          <Radial
            graphOptions={marketplaceFillRateOptions}
            dataTestId="marketplace-fill-rate"
          />
          <ChartName>Marketplace fill rate</ChartName>
        </Chart>
      </Charts>
    </Wrapper>
  );
};

FillRate.propTypes = {
  totalFillRate: PropTypes.number,
  pslFillRate: PropTypes.number,
  marketplaceFillRate: PropTypes.number,
  pdfExport: PropTypes.bool,
};

FillRate.defaultProps = {
  totalFillRate: 0,
  pslFillRate: 0,
  marketplaceFillRate: 0,
  pdfExport: false,
};

interface IReduxState {
  employerAnalytics: {
    agencyPerformanceData: {
      fillRate: {
        totalFillRate: number;
        pslFillRate: number;
        marketplaceFillRate: number;
      };
    };
  };
}

const mapStateToProps = (state: IReduxState) => ({
  totalFillRate:
    state.employerAnalytics.agencyPerformanceData.fillRate.totalFillRate,
  pslFillRate:
    state.employerAnalytics.agencyPerformanceData.fillRate.pslFillRate,
  marketplaceFillRate:
    state.employerAnalytics.agencyPerformanceData.fillRate.marketplaceFillRate,
});

export default connect(mapStateToProps, {})(FillRate);
